import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  DropdownArrow,
  DropdownIcon,
  DropdownItem,
  DropdownList,
  Flag,
  Input,
  InputFlag,
} from "./styles";
import { getContinentFromId } from "components/pages/GlobalRewards/helpers";
import { COUNTRY_DETAILS } from "components/pages/GlobalRewards/countryDetails";
import { RewardCountry } from "components/pages/GlobalRewards/types";

interface GlobalRewardsDropdown {
  countries: RewardCountry[];
  onCountryClick: (countryId: string) => void;
  onContinentClick: (continent: string) => void;
  resetCountryModalOnChange?: () => void;
  selectedCountry: string;
  isModalOpen: boolean;
}

export const GlobalRewardsDropdown: FC<GlobalRewardsDropdown> = ({
  countries,
  onCountryClick,
  onContinentClick,
  resetCountryModalOnChange,
  selectedCountry,
  isModalOpen,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setselectedOption] = useState(null);

  const filteredCountries = useMemo(() => {
    return countries.filter(
      (country) =>
        country.countryCode !== "US-main" &&
        country.countryName.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [countries, searchTerm]);

  const handleItemClick = useCallback(
    (country: RewardCountry) => {
      const { countryName, countryCode } = country;
      const continent = getContinentFromId(countryCode);

      onCountryClick(countryCode);
      setselectedOption(country);
      setSearchTerm(countryName);
      setIsOpen(false);
      onContinentClick(continent);
    },
    [setselectedOption, setSearchTerm, setIsOpen, onCountryClick, onContinentClick],
  );

  useEffect(() => {
    if (selectedCountry) {
      const { code: countryCode, country: countryName } = COUNTRY_DETAILS[selectedCountry];
      setselectedOption({ countryName, countryCode });
      setSearchTerm(countryName);
      setIsOpen(false);
    }
  }, [selectedCountry]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    resetDropdown(value);
    resetCountryModalOnChange?.();
  };

  useEffect(() => {
    if (!isModalOpen) {
      resetDropdown();
    }
  }, [isModalOpen]);

  const resetDropdown = (value: string = "") => {
    setSearchTerm(value);
    setselectedOption("");
  };

  const closeSelect = () => {
    resetCountryModalOnChange?.();
    resetDropdown();
    setIsOpen(false);
  };

  const getFlagFromCountryCode = useCallback((country: RewardCountry) => {
    return `/static/icons/flags/rewards-map/${country.countryCode} - ${country.countryName}.svg`;
  }, []);

  const searchBarEmpty = isOpen && !filteredCountries.length;

  return (
    <>
      <div>
        <Input
          type="text"
          placeholder="Select a country"
          value={selectedOption ? `${selectedOption.countryName}` : searchTerm}
          $countrySelected={!!selectedOption}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          isOpen={isOpen}
          $searchBarEmpty={searchBarEmpty}
          disabled={!!selectedOption}
        />
        {selectedOption ? (
          <>
            <InputFlag
              src={getFlagFromCountryCode(selectedOption)}
              alt={`${selectedOption.countryName} flag`}
            />
            <DropdownIcon
              src="/static/icons/Close.svg"
              width={24}
              height={24}
              alt="Close icon"
              onClick={() => closeSelect()}
            />
          </>
        ) : (
          <DropdownArrow
            $dropdownOpen={isOpen}
            src="/static/icons/DropdownIcon.svg"
            width={24}
            height={24}
            alt="Dropdown icon"
            onClick={() => setIsOpen(!isOpen)}
          />
        )}
      </div>
      <DropdownList isOpen={isOpen} $searchBarEmpty={searchBarEmpty}>
        {filteredCountries.map((country, index) => (
          <DropdownItem key={index} onMouseDown={() => handleItemClick(country)}>
            <Flag src={getFlagFromCountryCode(country)} alt={`${country.countryName} flag`} />
            <span>{country.countryName}</span>
          </DropdownItem>
        ))}
      </DropdownList>
    </>
  );
};

export default GlobalRewardsDropdown;
